import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Email,
  Sms,
  ContentCopy,
  Visibility,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body?: string;
  html?: string;
  createdAt?: string;
  updatedAt?: string;
  isDefault?: boolean;
}

interface SmsTemplate {
  id: string;
  name: string;
  message: string;
  createdAt?: string;
  updatedAt?: string;
  isDefault?: boolean;
}

const CampaignTemplates: React.FC = () => {
  const { agency } = useAuth();
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedEmailTemplate, setExpandedEmailTemplate] = useState<string | null>(null);
  const [expandedSmsTemplate, setExpandedSmsTemplate] = useState<string | null>(null);
  
  // Email template dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [viewingEmailTemplate, setViewingEmailTemplate] = useState<EmailTemplate | null>(null);
  const [editingEmailTemplate, setEditingEmailTemplate] = useState<EmailTemplate | null>(null);
  const [emailForm, setEmailForm] = useState({ name: '', subject: '', body: '', html: '' });
  const [deleteEmailDialogOpen, setDeleteEmailDialogOpen] = useState(false);
  const [emailTemplateToDelete, setEmailTemplateToDelete] = useState<EmailTemplate | null>(null);
  
  // SMS template dialog state
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [viewingSmsTemplate, setViewingSmsTemplate] = useState<SmsTemplate | null>(null);
  const [editingSmsTemplate, setEditingSmsTemplate] = useState<SmsTemplate | null>(null);
  const [smsForm, setSmsForm] = useState({ name: '', message: '' });
  const [deleteSmsDialogOpen, setDeleteSmsDialogOpen] = useState(false);
  const [smsTemplateToDelete, setSmsTemplateToDelete] = useState<SmsTemplate | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, [agency?.slug]);

  const fetchTemplates = async () => {
    if (!agency?.slug) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/agencies/${agency.slug}/campaign-templates`);
      // Ensure arrays are always set, even if undefined
      const emailTemplates = Array.isArray(res.data?.emailTemplates) ? res.data.emailTemplates : [];
      const smsTemplates = Array.isArray(res.data?.smsTemplates) ? res.data.smsTemplates : [];
      
      // Debug logging
      console.log('[CampaignTemplates Frontend] Received templates:', {
        emailCount: emailTemplates.length,
        smsCount: smsTemplates.length,
        emailTemplates: emailTemplates.map(t => ({ id: t.id, name: t.name, isDefault: t.isDefault })),
        smsTemplates: smsTemplates.map(t => ({ id: t.id, name: t.name, isDefault: t.isDefault })),
      });
      
      setEmailTemplates(emailTemplates);
      setSmsTemplates(smsTemplates);
    } catch (err: any) {
      console.error('[CampaignTemplates Frontend] Error fetching templates:', err);
      setError(err.response?.data?.error || 'Failed to load templates');
      // Set empty arrays on error to prevent undefined errors
      setEmailTemplates([]);
      setSmsTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyDefaultEmailTemplate = async (template: EmailTemplate) => {
    if (!agency?.slug) return;
    try {
      setError(null);
      await api.post(`/agencies/${agency.slug}/campaign-templates/email/copy-default`, {
        defaultTemplateId: template.id,
      });
      setSuccess('Default template copied to your agency templates');
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to copy template');
    }
  };

  const handleCopyDefaultSmsTemplate = async (template: SmsTemplate) => {
    if (!agency?.slug) return;
    try {
      setError(null);
      await api.post(`/agencies/${agency.slug}/campaign-templates/sms/copy-default`, {
        defaultTemplateId: template.id,
      });
      setSuccess('Default template copied to your agency templates');
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to copy template');
    }
  };

  const handleViewEmailTemplate = (template: EmailTemplate) => {
    setViewingEmailTemplate(template);
    setEditingEmailTemplate(null);
    setEmailDialogOpen(true);
  };

  const handleOpenEmailDialog = (template?: EmailTemplate) => {
    if (template) {
      // Prevent editing default templates
      if (template.isDefault) {
        setError('Default templates cannot be edited. Please copy the template first to create an editable agency-specific version.');
        return;
      }
      setEditingEmailTemplate(template);
      setViewingEmailTemplate(null);
      setEmailForm({
        name: template.name,
        subject: template.subject,
        body: template.body || '',
        html: template.html || '',
      });
    } else {
      setEditingEmailTemplate(null);
      setViewingEmailTemplate(null);
      setEmailForm({ name: '', subject: '', body: '', html: '' });
    }
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setEditingEmailTemplate(null);
    setViewingEmailTemplate(null);
    setEmailForm({ name: '', subject: '', body: '', html: '' });
  };

  const handleSaveEmailTemplate = async () => {
    if (!agency?.slug) return;
    if (!emailForm.name || !emailForm.subject || (!emailForm.body && !emailForm.html)) {
      setError('Name, subject, and either body or html are required');
      return;
    }
    
    try {
      setError(null);
      if (editingEmailTemplate) {
        await api.put(`/agencies/${agency.slug}/campaign-templates/email/${editingEmailTemplate.id}`, emailForm);
      } else {
        await api.post(`/agencies/${agency.slug}/campaign-templates/email`, emailForm);
      }
      setSuccess(editingEmailTemplate ? 'Email template updated successfully' : 'Email template created successfully');
      handleCloseEmailDialog();
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save email template');
    }
  };

  const handleDeleteEmailTemplateClick = (template: EmailTemplate) => {
    // Prevent deleting default templates
    if (template.isDefault) {
      setError('Default templates cannot be deleted. They are system templates that are always available. If you want to customize a default template, copy it first to create an agency-specific version.');
      return;
    }
    setEmailTemplateToDelete(template);
    setDeleteEmailDialogOpen(true);
  };

  const handleConfirmDeleteEmailTemplate = async () => {
    if (!agency?.slug || !emailTemplateToDelete) return;
    
    try {
      setError(null);
      await api.delete(`/agencies/${agency.slug}/campaign-templates/email/${emailTemplateToDelete.id}`);
      setSuccess('Email template deleted successfully');
      setDeleteEmailDialogOpen(false);
      setEmailTemplateToDelete(null);
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete email template');
    }
  };

  const handleViewSmsTemplate = (template: SmsTemplate) => {
    setViewingSmsTemplate(template);
    setEditingSmsTemplate(null);
    setSmsDialogOpen(true);
  };

  const handleOpenSmsDialog = (template?: SmsTemplate) => {
    if (template) {
      // Prevent editing default templates
      if (template.isDefault) {
        setError('Default templates cannot be edited. Please copy the template first to create an editable agency-specific version.');
        return;
      }
      setEditingSmsTemplate(template);
      setViewingSmsTemplate(null);
      setSmsForm({
        name: template.name,
        message: template.message,
      });
    } else {
      setEditingSmsTemplate(null);
      setViewingSmsTemplate(null);
      setSmsForm({ name: '', message: '' });
    }
    setSmsDialogOpen(true);
  };

  const handleCloseSmsDialog = () => {
    setSmsDialogOpen(false);
    setEditingSmsTemplate(null);
    setViewingSmsTemplate(null);
    setSmsForm({ name: '', message: '' });
  };

  const handleSaveSmsTemplate = async () => {
    if (!agency?.slug) return;
    if (!smsForm.name || !smsForm.message) {
      setError('Name and message are required');
      return;
    }
    
    try {
      setError(null);
      if (editingSmsTemplate) {
        await api.put(`/agencies/${agency.slug}/campaign-templates/sms/${editingSmsTemplate.id}`, smsForm);
      } else {
        await api.post(`/agencies/${agency.slug}/campaign-templates/sms`, smsForm);
      }
      setSuccess(editingSmsTemplate ? 'SMS template updated successfully' : 'SMS template created successfully');
      handleCloseSmsDialog();
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save SMS template');
    }
  };

  const handleDeleteSmsTemplateClick = (template: SmsTemplate) => {
    // Prevent deleting default templates
    if (template.isDefault) {
      setError('Default templates cannot be deleted. They are system templates that are always available. If you want to customize a default template, copy it first to create an agency-specific version.');
      return;
    }
    setSmsTemplateToDelete(template);
    setDeleteSmsDialogOpen(true);
  };

  const handleConfirmDeleteSmsTemplate = async () => {
    if (!agency?.slug || !smsTemplateToDelete) return;
    
    try {
      setError(null);
      await api.delete(`/agencies/${agency.slug}/campaign-templates/sms/${smsTemplateToDelete.id}`);
      setSuccess('SMS template deleted successfully');
      setDeleteSmsDialogOpen(false);
      setSmsTemplateToDelete(null);
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete SMS template');
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography>Loading templates...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 4 }}>
      <CardHeader
        title="Campaign Templates"
        subheader="Manage email and SMS templates for visitor campaigns"
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab icon={<Email />} iconPosition="start" label={`Email Templates (${emailTemplates.length})`} />
          <Tab icon={<Sms />} iconPosition="start" label={`SMS Templates (${smsTemplates.length})`} />
        </Tabs>

        {/* Email Templates Tab */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {emailTemplates.some(t => t.isDefault) 
                  ? 'Default templates (marked with "Default" badge) are system templates that are always available and cannot be edited or deleted. Copy a default template to create a customizable agency-specific version.'
                  : 'Create reusable email templates for bulk campaigns. Use variables like {{firstName}}, {{propertyAddress}}, etc.'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenEmailDialog()}
              >
                New Email Template
              </Button>
            </Box>
            {emailTemplates.length === 0 ? (
              <Alert severity="info">No email templates available. Default templates should be shown automatically.</Alert>
            ) : (
              <List>
                {emailTemplates.map((template) => {
                  const isExpanded = expandedEmailTemplate === template.id;
                  return (
                    <Box key={template.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">{template.name || 'Unnamed Template'}</Typography>
                              {template.isDefault && (
                                <Chip label="Default" size="small" color="primary" variant="outlined" />
                              )}
                              {!template.isDefault && (
                                <Chip label={template.html ? 'HTML' : 'Text'} size="small" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Subject: {template.subject}
                              </Typography>
                              {template.updatedAt && (
                                <Typography variant="caption" color="text.secondary">
                                  Updated: {new Date(template.updatedAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            onClick={() => setExpandedEmailTemplate(isExpanded ? null : template.id)}
                            title={isExpanded ? 'Collapse' : 'Expand to view'}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            onClick={() => handleViewEmailTemplate(template)}
                            title="View template"
                          >
                            <Visibility />
                          </IconButton>
                          {template.isDefault ? (
                            <IconButton 
                              edge="end" 
                              onClick={() => handleCopyDefaultEmailTemplate(template)}
                              title="Copy to agency templates to customize. Default templates cannot be edited or deleted - they are always available system templates."
                            >
                              <ContentCopy />
                            </IconButton>
                          ) : (
                            <>
                              <IconButton edge="end" onClick={() => handleOpenEmailDialog(template)} title="Edit template">
                                <Edit />
                              </IconButton>
                              <IconButton edge="end" onClick={() => handleDeleteEmailTemplateClick(template)} title="Delete template">
                                <Delete />
                              </IconButton>
                            </>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                      {isExpanded && (
                        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Subject:</Typography>
                          <Typography variant="body2" sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            {template.subject}
                          </Typography>
                          {template.body && (
                            <>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Body (Plain Text):</Typography>
                              <Typography variant="body2" sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
                                {template.body}
                              </Typography>
                            </>
                          )}
                          {template.html && (
                            <>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>HTML:</Typography>
                              <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                  {template.html}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>
        )}

        {/* SMS Templates Tab */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {smsTemplates.some(t => t.isDefault) 
                  ? 'Default templates (marked with "Default" badge) are system templates that are always available and cannot be edited or deleted. Copy a default template to create a customizable agency-specific version.'
                  : 'Create reusable SMS templates for bulk campaigns. Use variables like {{firstName}}, {{propertyAddress}}, etc.'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenSmsDialog()}
              >
                New SMS Template
              </Button>
            </Box>
            {smsTemplates.length === 0 ? (
              <Alert severity="info">No SMS templates available. Default templates should be shown automatically.</Alert>
            ) : (
              <List>
                {smsTemplates.map((template) => {
                  const isExpanded = expandedSmsTemplate === template.id;
                  return (
                    <Box key={template.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">{template.name || 'Unnamed Template'}</Typography>
                              {template.isDefault && (
                                <Chip label="Default" size="small" color="primary" variant="outlined" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {template.message && template.message.length > 100 
                                  ? `${template.message.substring(0, 100)}...` 
                                  : (template.message || 'No message')}
                              </Typography>
                              {template.updatedAt && (
                                <Typography variant="caption" color="text.secondary">
                                  Updated: {new Date(template.updatedAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            onClick={() => setExpandedSmsTemplate(isExpanded ? null : template.id)}
                            title={isExpanded ? 'Collapse' : 'Expand to view'}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            onClick={() => handleViewSmsTemplate(template)}
                            title="View template"
                          >
                            <Visibility />
                          </IconButton>
                          {template.isDefault ? (
                            <IconButton 
                              edge="end" 
                              onClick={() => handleCopyDefaultSmsTemplate(template)}
                              title="Copy to agency templates to customize. Default templates cannot be edited or deleted - they are always available system templates."
                            >
                              <ContentCopy />
                            </IconButton>
                          ) : (
                            <>
                              <IconButton edge="end" onClick={() => handleOpenSmsDialog(template)} title="Edit template">
                                <Edit />
                              </IconButton>
                              <IconButton edge="end" onClick={() => handleDeleteSmsTemplateClick(template)} title="Delete template">
                                <Delete />
                              </IconButton>
                            </>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                      {isExpanded && (
                        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Message:</Typography>
                          <Typography variant="body2" sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
                            {template.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Characters: {template.message.length} / 480 (approximately {Math.ceil(template.message.length / 160)} SMS message(s))
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>
        )}

        {/* Email Template Dialog */}
        <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {viewingEmailTemplate 
              ? `View Email Template: ${viewingEmailTemplate.name}${viewingEmailTemplate.isDefault ? ' (Default)' : ''}`
              : editingEmailTemplate 
                ? 'Edit Email Template' 
                : 'New Email Template'}
          </DialogTitle>
          <DialogContent>
            {viewingEmailTemplate ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Template Name:</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{viewingEmailTemplate.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Subject:</Typography>
                  <Typography variant="body1" sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {viewingEmailTemplate.subject}
                  </Typography>
                </Box>
                {viewingEmailTemplate.body && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Body (Plain Text):</Typography>
                    <Typography variant="body2" sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
                      {viewingEmailTemplate.body}
                    </Typography>
                  </Box>
                )}
                {viewingEmailTemplate.html && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>HTML:</Typography>
                    <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {viewingEmailTemplate.html}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <TextField
                  label="Template Name"
                  value={emailForm.name}
                  onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })}
                  fullWidth
                  required
                />
                <TextField
                  label="Subject"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  fullWidth
                  required
                  helperText="Use variables like {{firstName}}, {{propertyAddress}}, etc."
                />
                <TextField
                  label="Body (Plain Text)"
                  value={emailForm.body}
                  onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                  fullWidth
                  multiline
                  rows={6}
                  helperText="Plain text version. Leave empty if using HTML only."
                />
                <TextField
                  label="HTML"
                  value={emailForm.html}
                  onChange={(e) => setEmailForm({ ...emailForm, html: e.target.value })}
                  fullWidth
                  multiline
                  rows={8}
                  helperText="HTML version. Use variables like {{firstName}}, {{propertyAddress}}, etc."
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEmailDialog}>{viewingEmailTemplate ? 'Close' : 'Cancel'}</Button>
            {viewingEmailTemplate ? (
              <Button onClick={() => {
                handleCloseEmailDialog();
                handleOpenEmailDialog(viewingEmailTemplate);
              }} variant="contained">
                Edit
              </Button>
            ) : (
              <Button onClick={handleSaveEmailTemplate} variant="contained">
                {editingEmailTemplate ? 'Update' : 'Create'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* SMS Template Dialog */}
        <Dialog open={smsDialogOpen} onClose={handleCloseSmsDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {viewingSmsTemplate 
              ? `View SMS Template: ${viewingSmsTemplate.name}${viewingSmsTemplate.isDefault ? ' (Default)' : ''}`
              : editingSmsTemplate 
                ? 'Edit SMS Template' 
                : 'New SMS Template'}
          </DialogTitle>
          <DialogContent>
            {viewingSmsTemplate ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Template Name:</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{viewingSmsTemplate.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Message:</Typography>
                  <Typography variant="body2" sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
                    {viewingSmsTemplate.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Characters: {viewingSmsTemplate.message.length} / 480 (approximately {Math.ceil(viewingSmsTemplate.message.length / 160)} SMS message(s))
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <TextField
                  label="Template Name"
                  value={smsForm.name}
                  onChange={(e) => setSmsForm({ ...smsForm, name: e.target.value })}
                  fullWidth
                  required
                />
                <TextField
                  label="Message"
                  value={smsForm.message}
                  onChange={(e) => setSmsForm({ ...smsForm, message: e.target.value })}
                  fullWidth
                  required
                  multiline
                  rows={6}
                  helperText="Use variables like {{firstName}}, {{propertyAddress}}, etc. SMS messages are limited to 160 characters per message."
                  inputProps={{ maxLength: 480 }} // Allow for 3 SMS messages (160 * 3)
                />
                <Typography variant="caption" color="text.secondary">
                  Characters: {smsForm.message.length} / 480 (approximately {Math.ceil(smsForm.message.length / 160)} SMS message(s))
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSmsDialog}>{viewingSmsTemplate ? 'Close' : 'Cancel'}</Button>
            {viewingSmsTemplate ? (
              <Button onClick={() => {
                handleCloseSmsDialog();
                handleOpenSmsDialog(viewingSmsTemplate);
              }} variant="contained">
                Edit
              </Button>
            ) : (
              <Button onClick={handleSaveSmsTemplate} variant="contained">
                {editingSmsTemplate ? 'Update' : 'Create'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Delete Email Template Confirmation Dialog */}
        <Dialog
          open={deleteEmailDialogOpen}
          onClose={() => {
            setDeleteEmailDialogOpen(false);
            setEmailTemplateToDelete(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete color="error" />
            Delete Email Template
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>"{emailTemplateToDelete?.name}"</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setDeleteEmailDialogOpen(false);
                setEmailTemplateToDelete(null);
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteEmailTemplate}
              variant="contained"
              color="error"
              startIcon={<Delete />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete SMS Template Confirmation Dialog */}
        <Dialog
          open={deleteSmsDialogOpen}
          onClose={() => {
            setDeleteSmsDialogOpen(false);
            setSmsTemplateToDelete(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete color="error" />
            Delete SMS Template
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>"{smsTemplateToDelete?.name}"</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setDeleteSmsDialogOpen(false);
                setSmsTemplateToDelete(null);
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteSmsTemplate}
              variant="contained"
              color="error"
              startIcon={<Delete />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default CampaignTemplates;

